import { ImageOptionalLink } from '@rsa-digital/evo-shared-components/commonTypes/links';
import {
  PrimaryCta,
  SecondaryCta,
} from '@rsa-digital/evo-shared-components/components/Cta';
import { Grid, GridItem } from '@rsa-digital/evo-shared-components/components/Grid';
import { graphql } from 'gatsby';
import React from 'react';
import TwoColumnsActionCards from 'components/blocks/ActionCards/TwoColumnsActionCards';
import { ActionCardsData } from 'components/blocks/ActionCards/types';
import { RichTextBlock } from 'components/blocks/RichText';
import { RichTextData } from 'components/blocks/RichText/types';
import { processCta, processMandatoryImage } from 'helpers/csTypeProcessors';
import { handleAndTrackLinkClick } from 'helpers/navigation';
import { CsAsset, CsCta } from 'types/contentStack';
import { CtasContainer, LogoLinkGrid, StyledLogoLink } from './styles';

export type SignpostContentProps = {
  image_link: {
    image: CsAsset;
    url: string;
    screenreader_text: string;
  };
  heading?: string;
  rich_text?: RichTextData['rich_text'];
  ctas: CsCta[] | [];
  action_cards: ActionCardsData;
};

export const query = graphql`
  fragment SignpostContent on cs__signpost_content {
    image_link {
      image {
        ...Asset
      }
      url
      screenreader_text
    }
    heading
    rich_text {
      ...RichTextBlock
    }
    ctas {
      ...Cta
    }
    action_cards {
      ...ActionCardsBlock
    }
  }
`;

const processImageLink = (imageLinkData: {
  image: CsAsset;
  screenreader_text: string;
  url: string;
}): ImageOptionalLink | undefined => {
  return imageLinkData.image
    ? {
        link: {
          url: imageLinkData.url,
          screenReaderText: imageLinkData.screenreader_text,
          onClick: (e) =>
            handleAndTrackLinkClick(
              imageLinkData.url,
              'signpostContentImageLink',
              imageLinkData.screenreader_text
            )(e),
        },
        image: processMandatoryImage(imageLinkData.image),
      }
    : undefined;
};

const SignpostContent: React.FC<SignpostContentProps> = ({
  image_link,
  heading,
  rich_text,
  ctas,
  action_cards,
}: SignpostContentProps) => {
  const imageLink = processImageLink(image_link);
  return (
    <>
      {imageLink && (
        <LogoLinkGrid>
          <GridItem>
            <StyledLogoLink logoLink={imageLink} />
          </GridItem>
        </LogoLinkGrid>
      )}
      {heading && <h1>{heading}</h1>}
      {rich_text && <RichTextBlock rich_text={rich_text} />}
      {ctas && !!ctas.length && (
        <Grid>
          <GridItem>
            <CtasContainer>
              {ctas.map((cta, index) => {
                const ctaProps = processCta(cta, `SignpostContent-cta-${index}`);
                return index === 0 ? (
                  <PrimaryCta key={index} cta={ctaProps} />
                ) : (
                  <SecondaryCta key={index} cta={ctaProps} />
                );
              })}
            </CtasContainer>
          </GridItem>
        </Grid>
      )}
      {action_cards && !!action_cards.action_cards.length && (
        <TwoColumnsActionCards action_cards={action_cards} />
      )}
    </>
  );
};

export default SignpostContent;
